import { useState } from "react";
import CustomTable from "../../investorOverview/common/customTable";
import { PaginationObj, TableHeaderData } from "../../investorOverview/types"
import { Checkbox, IconButton, Menu, MenuItem, TableCell, TableRow, Typography, useTheme } from "@mui/material";
import { ActionIcon, CheckIcon, FailureIcon, SuccessTickIcon, UnCheckIcon } from "../../../assets/SvgIcons";
import { InvoiceControlListObj } from "../../../redux-store/types/onBoarding";

const headerData: TableHeaderData[] = [
    {
        // Leading selection column. Its header label is replaced at render time
        // with a "select all" checkbox via `labelComponent` (see below).
        id: 'select',
        label: '',
        align: 'left',
        isSortable: false,
    },
    {
        id: 'rm_name',
        label: 'RM Name',
        align: 'left',
        isSortable: false,
    },
    {
        id: 'family_name',
        label: 'Family Name',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'billing_entity_name',
        label: 'Member Name',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'type',
        label: 'Type',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'invoice_state',
        label: 'Status',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'invoiceEmail',
        label: 'Invoice Shared',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'invoiceDownload',
        label: 'Invoice Downloaded',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'action',
        label: '',
        align: 'center',
        isSortable: false,
    }
]

// Row action labels per invoice status. ReportGeneration.tsx's
// handleMenuItemClick lower-cases each label before matching it, so
// "Generate Report" here resolves to the "generate report" case there.
// Keep these strings and those switch cases aligned.
const ACTION_MENU_MAP: Record<string, Array<string>> = {
    freeze: [
        "Unfreeze",
        "Generate Report"
    ],
    freezed: [
        "Unfreeze",
        "Generate Report"
    ],
    unfreeze: [
        "Freeze",
        "Generate Report"
    ],
    unfreezed: [
        "Freeze",
        "Generate Report"
    ],
    not_generated: [
        "Generate Report"
    ]
}

const STATUS_COLOR_MAP: Record<string, string> = {
    freeze: "primary.500",
    freezed: "primary.500",
    unfreeze: "primary.200",
    unfreezed: "primary.200",
}

function ActionMenu({
    row,
    handleMenuItemClick
}: {
    row: InvoiceControlListObj;
    handleMenuItemClick: (selectedMenuItem: string, currentRow: InvoiceControlListObj) => void;
}) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleItemClick = (item: string) => {
        handleMenuItemClick(item, row)

        handleClose()
    }

    return (
        <>
            <IconButton
                size="small"
                onClick={handleOpen}
                aria-controls={open ? `action-menu-${row.rm_name}-${row.family_id}` : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                sx={{
                    '&.Mui-disabled svg': {
                        opacity: 0.38,
                    }
                }}
            >
                <ActionIcon />
            </IconButton>
            <Menu
                id={`action-menu-${row.rm_name}-${row.family_id}`}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                {(ACTION_MENU_MAP[`${row?.invoice_state ?? ""}`.toLowerCase()] ?? []).map(item => (
                    <MenuItem
                        key={item}
                        onClick={() => handleItemClick(item)}
                    >
                        {item}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

function DataTable({
    tableBodyData,
    handleMenuItemClick,
    handleCheckboxClick
}: {
    tableBodyData: Array<InvoiceControlListObj>;
    handleMenuItemClick: (selectedMenuItem: string, currentRow: InvoiceControlListObj) => void;
    handleCheckboxClick: (currentRow: InvoiceControlListObj, checked: boolean) => void;
}): JSX.Element {
    const theme = useTheme()

    return (
        <>
            {
                tableBodyData.map((obj: InvoiceControlListObj, index: number) => {
                    return (
                        <TableRow
                            key={`${obj.rm_name}-${obj.family_id}-${index}`}
                            sx={{
                                height: "4.5em",
                                '& td': {
                                    padding: "5px",
                                    height: "4.5em",
                                    borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                                },
                                '& td:first-of-type': {
                                    paddingLeft: "15px"
                                },
                            }}
                        >
                            {/* Per-row selection checkbox. `obj.checked` is derived by the
                                parent from the selected-row keys; toggling it bubbles the row
                                and its new checked state up via handleCheckboxClick. */}
                            <TableCell align="left">
                                <Checkbox
                                    size="small"
                                    checkedIcon={<CheckIcon />}
                                    icon={<UnCheckIcon />}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                    checked={!!obj?.checked}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleCheckboxClick(obj, event.target.checked)}
                                    inputProps={{
                                        'aria-label': `Select ${obj?.family_name ?? ""} ${obj?.billing_entity_name ?? ""}`,
                                    }}
                                />
                            </TableCell>
                            <TableCell align="left">
                                <Typography
                                    variant="body_500"
                                    sx={{
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    {obj?.rm_name ? obj?.rm_name : "-"}
                                </Typography>
                                {obj?.rm_id ? (
                                    <Typography
                                        variant="body_500"
                                        sx={{
                                            lineHeight: "20px",
                                            letterSpacing: "0.02px",
                                            color: theme.palette.text.primary,
                                        }}
                                    >
                                        ({obj.rm_id})
                                    </Typography>
                                ) : null}
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body_500"
                                    sx={{
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    {obj?.family_name ? obj?.family_name : "-"}
                                </Typography>
                                {obj?.family_id ? (
                                    <Typography
                                        variant="body_500"
                                        sx={{
                                            lineHeight: "20px",
                                            letterSpacing: "0.02px",
                                            color: theme.palette.text.primary,
                                        }}
                                    >
                                        ({obj.family_id})
                                    </Typography>
                                ) : null}
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body_500"
                                    sx={{
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    {obj?.billing_entity_name ? obj?.billing_entity_name : "-"}
                                </Typography>
                                {obj?.billing_entity_id ? (
                                    <Typography
                                        variant="body_500"
                                        sx={{
                                            lineHeight: "20px",
                                            letterSpacing: "0.02px",
                                            color: theme.palette.text.primary,
                                        }}
                                    >
                                        ({obj.billing_entity_id})
                                    </Typography>
                                ) : null}
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body_500"
                                    sx={{
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    {obj?.type || "-"}
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body_500"
                                    sx={{
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: STATUS_COLOR_MAP[`${obj?.invoice_state ?? ""}`.toLowerCase()] ?? theme.palette.text.primary,
                                        marginLeft: "10px"
                                    }}
                                >
                                    {obj?.invoice_state || "-"}
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                {((obj?.invoiceEmail ?? "") === "1") ? <SuccessTickIcon /> : <FailureIcon width="20" height="30" />}
                            </TableCell>
                            <TableCell align="center">
                                {((obj?.invoiceDownload ?? "") === "1") ? <SuccessTickIcon /> : <FailureIcon width="20" height="30" />}
                            </TableCell>
                            <TableCell align="center">
                                <ActionMenu row={obj} handleMenuItemClick={handleMenuItemClick} />
                            </TableCell>
                        </TableRow >
                    )
                })
            }
        </>
    )
}

export default function ReportGenerationTable({
    isLoading,
    tableData,
    tablePaginationObj,
    handleTableRowsPerPageChange,
    handleTablePageChange,
    handleMenuItemClick,
    handleCheckboxClick,
    handleSelectAllClick,
    isAllSelected,
    isSomeSelected
}: {
    isLoading: boolean;
    tableData: Array<InvoiceControlListObj>;
    tablePaginationObj: PaginationObj;
    handleTableRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleTablePageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
    handleMenuItemClick: (selectedMenuItem: string, currentRow: InvoiceControlListObj) => void;
    handleCheckboxClick: (currentRow: InvoiceControlListObj, checked: boolean) => void;
    handleSelectAllClick: (checked: boolean) => void;
    isAllSelected: boolean;
    isSomeSelected: boolean;
}): JSX.Element {
    // "Select all" header checkbox. Shows an indeterminate state when only some
    // rows are selected, and checked when all are. The toggle target is inverted
    // from the current state: if anything is selected, clicking clears; otherwise
    // it selects all. Disabled while loading or when there is no data.
    const selectAllHeader = (
        <Checkbox
            size="small"
            checkedIcon={<CheckIcon />}
            icon={<UnCheckIcon />}
            indeterminate={isSomeSelected && !isAllSelected}
            checked={isAllSelected}
            disabled={isLoading || !tableData.length}
            sx={{
                '&:hover': {
                    backgroundColor: 'transparent',
                },
            }}
            onChange={() => handleSelectAllClick(!(isAllSelected || isSomeSelected))}
            inputProps={{
                'aria-label': 'Select all rows',
            }}
        />
    )

    // Inject the select-all checkbox into the leading 'select' header cell. All
    // other headers are passed through unchanged.
    const tableHeaderData: TableHeaderData[] = headerData.map(header =>
        header.id === 'select' ? { ...header, labelComponent: selectAllHeader } : header
    )

    return (
        <CustomTable
            isLoading={isLoading}
            tableBody={
                <DataTable
                    tableBodyData={tableData}
                    handleMenuItemClick={handleMenuItemClick}
                    handleCheckboxClick={handleCheckboxClick}
                />
            }
            tableData={tableData}
            tableHeaderData={tableHeaderData}
            tableHeadRowCustomStyles={{
                '& th:first-of-type': {
                    paddingLeft: "15px"
                }
            }}
            tableBodyCustomStyles={{
                '& tr:last-child td': {
                    border: 0
                }
            }}
            loadingRowsNum={7}
            tableDataCount={tablePaginationObj.totalRows}
            rowsPerPage={tablePaginationObj.rowsPerPage}
            rowsPerPageOptions={[5, 10, 15, 20]}
            handleRowsPerPageChange={handleTableRowsPerPageChange}
            handlePageChange={handleTablePageChange}
            currentPage={tablePaginationObj.currentPage}
        />
    )
}